/**
 * A function to get a promise that will defer it's settling on subsequent
 *   repaint cycles.
 *
 * @module 00-utilities/repaintPromise
 *
 * @author [Sergio Jiménez Herena] <sergio.jimenez.herena@gmail.com>
 */

/**
 * Returns a promise with the settle procedure deferred to next repaint cycle.
 *
 * @param {number} maxCycles
 *   The maximum number of repaint cycles before settling the promise.
 * @param {Function} test
 *   A function to execute in each repaint cycle, if it's return value is
 *     truthy, the promise will resolve.
 * @returns {Promise}
 *   Will reject if maxCycles is reached, or resolve if the test is passed
 *     before.
 */
function repaintPromise( maxCycles = 1, test = () => false ) {
  let
    cyclesPerformed = 0;
  return new Promise(( resolve, reject ) => {
    /**
     * Will conditionally settle the promise or queue a new test in the
     *   next repaint cycle.
     */
    function settleNow() {
      if ( cyclesPerformed === maxCycles ) {
        reject();
      } else if ( test()) {
        resolve();
      } else {
        settleLater();
        ++cyclesPerformed;
      }
    }
    /**
     * Queue a test to settle the promise in the next repaint cycle.
     */
    function settleLater() {
      requestAnimationFrame( settleNow );
    }
    settleLater();
  });
}

export default repaintPromise;
